/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 创建团队面板
 */
import React, { useState, useEffect } from 'react'
import { useAppSelector, useAppDispatch } from '../../../app/hooks'
import { Button, Form, Input, message, TreeSelect } from 'antd'
import type { ValidateStatus } from 'antd/es/form/FormItem'
import todaycam_logo from '../../../images/todaycam_logo.png'
import { calcCharactersLength } from '../../../utils/utils'
import { setGroupId } from '../../../actions/group'
import sensors from '../../../utils/sensors'
import { capturePromiseException } from '../../../utils/sentryUtils'
import {
  fetchIndustryList,
  createGroup,
  fetchUser
} from '../../../actions/main'
import './index.scss'

interface IndustryItem {
  code: string
  name: string
  children?: IndustryItem[]
}

const generateTreeData = (data: IndustryItem[]): any[] => {
  return data.map(({ code, name, children = [] }) => {
    const hasChildren = children && children.length > 0
    return {
      title: name,
      value: code,
      key: code,
      // 与移动端对齐：只允许选叶子节点（二级行业），一级置灰不可选
      selectable: !hasChildren,
      disabled: hasChildren,
      children: generateTreeData(children)
    }
  })
}

// 与移动端对齐：禁用 emoji 与特殊符号
// 仅保留中英文、数字、基础标点（中划线/下划线/空格/点/括号/顿号）
const sanitizeGroupName = (raw: string): string => {
  if (!raw) return ''
  // 1) 剔除 emoji（Unicode 扩展区段）+ 变体选择符 + 零宽连接符
  const emojiRe =
    // eslint-disable-next-line no-misleading-character-class
    /[\u{1F300}-\u{1FAFF}\u{1F000}-\u{1F2FF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}]/gu
  let s = raw.replace(emojiRe, '')
  // 2) 剔除常见非法符号：\ / : * ? " < > | & ^ ~ ` 等
  s = s.replace(/[\\/:*?"<>|&^~`$@#%=+{}[\];]/g, '')
  return s
}

interface CreateGroupPanelProps {
  fromPlace?: string
  onGotoJoin?: () => void
}

function CreateGroupPanel(props: CreateGroupPanelProps) {
  const { fromPlace, onGotoJoin } = props
  const dispatch = useAppDispatch()
  const userID = useAppSelector((state) => state.main.user?.user_id)

  const [groupName, setGroupName] = useState('')
  const [validateStatus, setValidateStatus] = useState<ValidateStatus>('')
  const [groupNameHelp, setGroupNameHelp] = useState('')
  const [industryType, setIndustryType] = useState<string | undefined>(
    undefined
  )
  const [industryList, setIndustryList] = useState<IndustryItem[]>([])

  const trackCreate = (
    actionType: string,
    extras: Record<string, any> = {}
  ) => {
    sensors.track('web_workgroup_create_newgroup', {
      actionType,
      fromPlace: fromPlace ? fromPlace : 'navMenu',
      track_sign: 'aworkgroup.b4408.action.web_workgroup_create_newgroup',
      requirement_id: 1090,
      ...extras
    })
  }

  useEffect(() => {
    trackCreate('show')
    dispatch(fetchIndustryList()).then((res) => {
      setIndustryList(res.industry_types)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const handleGroupNameChange = (e) => {
    const cleaned = sanitizeGroupName(e.target.value)
    setGroupName(cleaned)
    if (!calcCharactersLength(cleaned, 32)) {
      setValidateStatus('error')
      setGroupNameHelp('团队名称最多可输入16个汉字')
    } else {
      setValidateStatus('')
      setGroupNameHelp('')
    }
  }

  const handleGotoJoin = () => {
    trackCreate('clickToJoin')
    if (onGotoJoin) {
      onGotoJoin()
    }
  }

  const handleSubmit = () => {
    const trimmedName = groupName.trim()
    if (!trimmedName) {
      message.warning('请输入团队名称')
      return
    }
    dispatch(
      createGroup({
        userID,
        groupName: trimmedName,
        industryCode: industryType
      })
    )
      .then((data) => {
        trackCreate('clickCreate', {
          groupName: trimmedName,
          groupID: data.groupID,
          industrialClassification: industryType,
          isAutoCreateGroup: false
        })
        message.success('团队创建成功，欢迎你')
        if (data.groupID) {
          localStorage.setItem('group_id', data.groupID)
          dispatch(setGroupId(data.groupID))
        }
        // 第一次创建团队时存在 race：reload 后并发的 fetchUserPoliceList 等接口
        // 可能在后端 session/cookie 把新团队关联完成前命中 401，被 fetchHelper 弹
        // "登录状态失效"。currentuser 接口在 fetchHelper 401 白名单里、不弹提示，
        // 主动等它完成相当于探测后端 session 已就绪，再 reload 即可消除误弹。
        const goReload = () => window.location.reload()
        dispatch(fetchUser()).then(goReload, goReload)
      })
      .catch(capturePromiseException)
  }

  const treeData = generateTreeData(industryList)

  return (
    <div className="create-group">
      <img className="logo" src={todaycam_logo} alt="今日水印相机" />
      <h3 className="title" id="title">
        创建团队
      </h3>

      <Form className="create-form">
        <Form.Item validateStatus={validateStatus} help={groupNameHelp}>
          <Input
            size="large"
            placeholder="请输入团队名称"
            value={groupName}
            onChange={handleGroupNameChange}
          />
        </Form.Item>

        <Form.Item>
          <TreeSelect
            treeData={treeData}
            value={industryType}
            onChange={(value: string) => {
              setIndustryType(value)
            }}
            placeholder="请选择行业"
            dropdownStyle={{
              height: 180
            }}
          />
        </Form.Item>

        <Form.Item>
          <Button
            className="cerate-button"
            type="primary"
            disabled={!!validateStatus || !groupName.trim() || !industryType}
            onClick={handleSubmit}
          >
            创建
          </Button>
        </Form.Item>
      </Form>

      {onGotoJoin && (
        <div className="create-group-footer">
          已有团队？
          <a className="create-group-footer-link" onClick={handleGotoJoin}>
            去加入
          </a>
        </div>
      )}
    </div>
  )
}

export default CreateGroupPanel
